import { Component, Input, OnInit } from '@angular/core';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { ToastrService } from 'ngx-toastr';
import { AnchorService } from 'src/app/_services/anchor.service';
import { StageService } from 'src/app/_services/stage.service';
import { ModalService } from 'src/app/_services/modal.service';

interface DeleteCheckData {
  mmitaRewardIds: string[];
  campaignWaveIds: string[];
  streamEventIds: string[];
  arIds: string[];
  stageIds: string[];
}

interface Section {
  title: string;
  key: string;
  items: string[];
  showDeleteBtn: boolean;
  selectedItems: Set<string>;
}

@Component({
  selector: 'app-delete-check-dialog',
  templateUrl: './delete-check-dialog.component.html',
  styleUrls: ['./delete-check-dialog.component.scss']
})
export class DeleteCheckDialogComponent implements OnInit {
  @Input() deleteCheckData: DeleteCheckData;
  @Input() onConfirmDelete: () => void;

  expandedSection = {
    mmitaRewardIds: true,
    campaignWaveIds: true,
    streamEventIds: true,
    arIds: true,
    stageIds: true
  };

  sections: Section[] = [];
  isDeleting = false;

  constructor(
    private modalRef: NzModalRef,
    private toastr: ToastrService,
    private anchorService: AnchorService,
    private stageService: StageService,
    private modalService: ModalService
  ) { }

  ngOnInit() {
    this.initializeSections();
  }

  private initializeSections() {
    this.sections = [
      {
        title: 'MMITA Reward IDs',
        key: 'mmitaRewardIds',
        items: this.deleteCheckData?.mmitaRewardIds || [],
        showDeleteBtn: false,
        selectedItems: new Set()
      },
      {
        title: 'Campaign Wave IDs',
        key: 'campaignWaveIds',
        items: this.deleteCheckData?.campaignWaveIds || [],
        showDeleteBtn: false,
        selectedItems: new Set()
      },
      {
        title: 'Stream Event IDs',
        key: 'streamEventIds',
        items: this.deleteCheckData?.streamEventIds || [],
        showDeleteBtn: false,
        selectedItems: new Set()
      },
      {
        title: 'AR IDs',
        key: 'arIds',
        items: this.deleteCheckData?.arIds || [],
        showDeleteBtn: true,
        selectedItems: new Set()
      },
      {
        title: 'Stage IDs',
        key: 'stageIds',
        items: this.deleteCheckData?.stageIds || [],
        showDeleteBtn: true,
        selectedItems: new Set()
      }
    ];
  }

  getDisplayData() {
    return this.sections.filter(section => section.items && section.items.length > 0);
  }

  toggleSection(key: string) {
    this.expandedSection[key] = !this.expandedSection[key];
  }

  toggleItemSelection(section: Section, item: string) {
    if (section.selectedItems.has(item)) {
      section.selectedItems.delete(item);
    } else {
      section.selectedItems.add(item);
    }
  }

  isItemSelected(section: Section, item: string): boolean {
    return section.selectedItems.has(item);
  }

  isAllSelected(section: Section): boolean {
    return section.items.length > 0 && section.selectedItems.size === section.items.length;
  }

  toggleSelectAll(section: Section) {
    if (this.isAllSelected(section)) {
      section.selectedItems.clear();
    } else {
      section.items.forEach(item => section.selectedItems.add(item));
    }
  }

  canDelete(): boolean {
    return !this.hasAnyItems();
  }

  hasAnyItems(): boolean {
    return this.sections.some(section => section.items && section.items.length > 0);
  }

  batchDeleteAr() {
    const arSection = this.sections.find(s => s.key === 'arIds');
    if (!arSection || arSection.selectedItems.size === 0) {
      this.toastr.warning('Please select AR IDs to delete');
      return;
    }

    this.isDeleting = true;
    this.anchorService.batchDelete({ ids: Array.from(arSection.selectedItems) }).subscribe(
      () => {
        this.toastr.success('AR records deleted successfully');
        arSection.items = arSection.items.filter(item => !arSection.selectedItems.has(item));
        arSection.selectedItems.clear();
        this.isDeleting = false;
      },
      (error) => {
        this.toastr.error('Failed to delete AR records');
        this.isDeleting = false;
      }
    );
  }

  batchDeleteStage() {
    const stageSection = this.sections.find(s => s.key === 'stageIds');
    if (!stageSection || stageSection.selectedItems.size === 0) {
      this.toastr.warning('Please select Stage IDs to delete');
      return;
    }

    this.isDeleting = true;
    this.stageService.batchDelete(Array.from(stageSection.selectedItems)).subscribe(
      () => {
        this.toastr.success('Stage records deleted successfully');
        stageSection.items = stageSection.items.filter(item => !stageSection.selectedItems.has(item));
        stageSection.selectedItems.clear();
        this.isDeleting = false;
      },
      (error) => {
        this.toastr.error('Failed to delete Stage records');
        this.isDeleting = false;
      }
    );
  }

  canConfirmDelete(): boolean {
    return !this.hasAnyItems();
  }

  onDelete() {
    if (this.hasAnyItems()) {
      this.toastr.warning('Please delete all associated items before deleting the asset');
      return;
    }
    this.onConfirmDelete();
    this.modalRef.close();
  }

  onCancel() {
    this.modalRef.close();
  }

  showArDetail(arId: string) {
    this.anchorService.getAR(arId).subscribe(
      (data) => {
        this.modalService.showDetail(data, 'AR Detail');
      },
      (error) => {
        this.toastr.error('Failed to load AR details', 'Error');
      }
    );
  }

  showStageDetail(stageId: string) {
    this.stageService.getStageById(stageId).subscribe(
      (data) => {
        this.modalService.showDetail(data, 'Stage Detail');
      },
      (error) => {
        this.toastr.error('Failed to load Stage details', 'Error');
      }
    );
  }
}

